/**
 * Разбор параметров списка тем и построение ссылок.
 *
 * Один модуль на роут и на компонент, чтобы ссылки в пагинации и запрос
 * к GraphQL не разъехались: URL — единственный источник состояния списка,
 * никакого состояния в компоненте нет.
 */

/** Сколько тем на странице. Совпадает с limit в GraphQL. */
export const PER_PAGE = 24;

/**
 * Сортировки, доступные из интерфейса. Ключ — то, что стоит в URL,
 * `order` — то, что уходит в GraphQL (там свой белый список).
 */
export const SORTS = [
	{ key: 'popular', label: 'Popular', order: 'installs DESC' },
	{ key: 'new', label: 'Newest', order: 'createdAt DESC' },
	{ key: 'title', label: 'A–Z', order: 'title ASC' },
];

export const DEFAULT_SORT = 'popular';

/**
 * Фильтр по версии расширения, в которой сделана тема. Ключ — в URL
 * (?made=new), `value` — в GraphQL (поле Theme.madeWith).
 */
export const MADES = [
	{ key: '', label: 'All themes', value: null },
	{ key: 'new', label: 'Made in 4.0', value: '4.0' },
	{ key: 'classic', label: 'Classic', value: 'classic' },
];

/**
 * Приводит query-параметры к нормальному виду. Мусор молча превращается
 * в значения по умолчанию: страница списка не должна падать из-за того,
 * что кто-то поправил адрес руками.
 *
 * @param {Object} query
 * @returns {{page: number, sort: string, search: string, order: string, made: string, madeWith: (string|null)}}
 */
export function parseThemesQuery(query = {}) {
	const page = Math.max(1, Math.min(parseInt(query.page, 10) || 1, 1000));

	const sort = SORTS.some(s => s.key === query.sort)
		? query.sort
		: DEFAULT_SORT;

	const search =
		typeof query.q === 'string' ? query.q.trim().slice(0, 60) : '';

	const madeOption =
		MADES.find(m => m.key && m.key === query.made) || MADES[0];

	return {
		page,
		sort,
		search,
		order: SORTS.find(s => s.key === sort).order,
		made: madeOption.key,
		madeWith: madeOption.value,
	};
}

/**
 * Адрес списка с заданными параметрами. Значения по умолчанию в URL не
 * пишутся: /themes и /themes?page=1&sort=popular — это одна страница,
 * и плодить её варианты незачем.
 *
 * @param {{page?: number, sort?: string, search?: string, made?: string}} state
 * @returns {string}
 */
export function themesUrl({
	page = 1,
	sort = DEFAULT_SORT,
	search = '',
	made = '',
} = {}) {
	const parts = [];

	if (search) {
		parts.push(`q=${encodeURIComponent(search)}`);
	}

	if (made) {
		parts.push(`made=${made}`);
	}

	if (sort && sort !== DEFAULT_SORT) {
		parts.push(`sort=${sort}`);
	}

	if (page > 1) {
		parts.push(`page=${page}`);
	}

	return parts.length ? `/themes?${parts.join('&')}` : '/themes';
}

/**
 * Номера страниц для пагинации: первая, последняя, текущая и соседи.
 * Пропуски обозначены null — компонент рисует на их месте многоточие.
 *
 * @param {number} current
 * @param {number} total
 * @returns {Array<number|null>}
 */
export function pageNumbers(current, total) {
	if (total <= 7) {
		return Array.from({ length: total }, (_, i) => i + 1);
	}

	const pages = new Set([1, total, current]);

	if (current - 1 > 1) pages.add(current - 1);
	if (current + 1 < total) pages.add(current + 1);

	const sorted = [...pages].sort((a, b) => a - b);
	const result = [];

	sorted.forEach((page, i) => {
		if (i > 0 && page - sorted[i - 1] > 1) {
			result.push(null);
		}

		result.push(page);
	});

	return result;
}
